import { CommunityStatus, Language } from '@/types';
import { STATUS_THRESHOLDS } from '@/constants';

/**
 * Calculate community status based on total exchanges
 */
export const calculateCommunityStatus = (totalExchanges: number): CommunityStatus => {
  if (totalExchanges >= STATUS_THRESHOLDS.COMMUNITY_PILLAR) return 'community_pillar';
  if (totalExchanges >= STATUS_THRESHOLDS.LOCAL_CONNECTOR) return 'local_connector';
  if (totalExchanges >= STATUS_THRESHOLDS.HELPING_HAND) return 'helping_hand';
  return 'new_neighbor';
};

/**
 * Get status display string with emoji
 */
export const getStatusDisplay = (status: CommunityStatus, language: Language): string => {
  const statusMap: Record<CommunityStatus, Record<Language, string>> = {
    new_neighbor: {
      en: '🌱 New Neighbor',
      fr: '🌱 Nouveau voisin',
    },
    helping_hand: {
      en: '🤝 Helping Hand',
      fr: '🤝 Coup de main',
    },
    local_connector: {
      en: '💬 Local Connector',
      fr: '💬 Connecteur local',
    },
    community_pillar: {
      en: '🌟 Community Pillar',
      fr: '🌟 Pilier de la communauté',
    },
  };

  return statusMap[status][language];
};

/**
 * Get progress to next status level
 */
export const getStatusProgress = (totalExchanges: number): {
  current: CommunityStatus;
  next: CommunityStatus | null;
  progress: number;
  remaining: number;
} => {
  const current = calculateCommunityStatus(totalExchanges);

  const levels: { status: CommunityStatus; threshold: number }[] = [
    { status: 'new_neighbor', threshold: STATUS_THRESHOLDS.NEW_NEIGHBOR },
    { status: 'helping_hand', threshold: STATUS_THRESHOLDS.HELPING_HAND },
    { status: 'local_connector', threshold: STATUS_THRESHOLDS.LOCAL_CONNECTOR },
    { status: 'community_pillar', threshold: STATUS_THRESHOLDS.COMMUNITY_PILLAR },
  ];

  const currentIndex = levels.findIndex((l) => l.status === current);
  const nextLevel = levels[currentIndex + 1];

  if (!nextLevel) {
    return {
      current,
      next: null,
      progress: 100,
      remaining: 0,
    };
  }

  const currentThreshold = levels[currentIndex]?.threshold ?? 0;
  const nextThreshold = nextLevel.threshold;
  const range = nextThreshold - currentThreshold;
  const progress = ((totalExchanges - currentThreshold) / range) * 100;
  const remaining = nextThreshold - totalExchanges;

  return {
    current,
    next: nextLevel.status,
    progress: Math.min(100, Math.max(0, progress)),
    remaining: Math.max(0, remaining),
  };
};
